import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { adminApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Separator } from '../../components/ui/separator';
import {
  Settings,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    low_threshold: 15,
    moderate_threshold: 30,
    high_threshold: 50,
    max_file_size_mb: 100,
    exclude_bibliography: true,
    exclude_quotes: true,
    exclude_small_matches: 5,
    enable_internal_repo: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await adminApi.getSettings();
        setSettings(response.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      await adminApi.updateSettings(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl" data-testid="admin-settings-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">System Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure similarity thresholds, file limits, and detection rules
          </p>
        </div>

        {success && (
          <Alert className="bg-emerald-50 border-emerald-200">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800">
              Settings saved successfully
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Similarity Thresholds */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">Similarity Thresholds</CardTitle>
            <CardDescription>
              Define the percentage thresholds for risk level classification
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="low_threshold">Low Risk (below %)</Label>
                <Input
                  id="low_threshold"
                  type="number"
                  min="0"
                  max="100"
                  value={settings.low_threshold}
                  onChange={(e) => handleChange('low_threshold', parseInt(e.target.value))}
                  data-testid="low-threshold-input"
                  className="h-12"
                />
                <p className="text-xs text-muted-foreground">
                  Submissions below this % are considered low risk
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="moderate_threshold">Moderate Risk (below %)</Label>
                <Input
                  id="moderate_threshold"
                  type="number"
                  min="0"
                  max="100"
                  value={settings.moderate_threshold}
                  onChange={(e) => handleChange('moderate_threshold', parseInt(e.target.value))}
                  data-testid="moderate-threshold-input"
                  className="h-12"
                />
                <p className="text-xs text-muted-foreground">
                  Submissions below this % but above low are moderate risk
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="high_threshold">High Risk (above %)</Label>
                <Input
                  id="high_threshold"
                  type="number"
                  min="0"
                  max="100"
                  value={settings.high_threshold}
                  onChange={(e) => handleChange('high_threshold', parseInt(e.target.value))}
                  data-testid="high-threshold-input"
                  className="h-12"
                />
                <p className="text-xs text-muted-foreground">
                  Reference value for high risk alerts
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* File Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">File Settings</CardTitle>
            <CardDescription>
              Configure file upload limits
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-w-xs">
              <Label htmlFor="max_file_size">Maximum File Size (MB)</Label>
              <Input
                id="max_file_size"
                type="number"
                min="1"
                max="100"
                value={settings.max_file_size_mb}
                onChange={(e) => handleChange('max_file_size_mb', parseInt(e.target.value))}
                data-testid="max-file-size-input"
                className="h-12"
              />
              <p className="text-xs text-muted-foreground">
                Maximum allowed file size for uploads
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Exclusion Rules */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">Exclusion Rules</CardTitle>
            <CardDescription>
              Configure what content should be excluded from similarity checks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="exclude_bibliography" className="text-base">
                  Exclude Bibliography / References
                </Label>
                <p className="text-sm text-muted-foreground">
                  Ignore matches found in bibliography sections
                </p>
              </div>
              <Switch
                id="exclude_bibliography"
                checked={settings.exclude_bibliography}
                onCheckedChange={(checked) => handleChange('exclude_bibliography', checked)}
                data-testid="exclude-bibliography-switch"
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="exclude_quotes" className="text-base">
                  Exclude Quoted Text
                </Label>
                <p className="text-sm text-muted-foreground">
                  Ignore matches found within quotation marks
                </p>
              </div>
              <Switch
                id="exclude_quotes"
                checked={settings.exclude_quotes}
                onCheckedChange={(checked) => handleChange('exclude_quotes', checked)}
                data-testid="exclude-quotes-switch"
              />
            </div>

            <Separator />

            <div className="space-y-2 max-w-xs">
              <Label htmlFor="exclude_small_matches">Minimum Match Length (words)</Label>
              <Input
                id="exclude_small_matches"
                type="number"
                min="1"
                max="50"
                value={settings.exclude_small_matches}
                onChange={(e) => handleChange('exclude_small_matches', parseInt(e.target.value))}
                data-testid="exclude-small-matches-input"
                className="h-12"
              />
              <p className="text-xs text-muted-foreground">
                Ignore matches shorter than this word count
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Repository Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">Internal Repository</CardTitle>
            <CardDescription>
              Configure internal document comparison settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enable_internal_repo" className="text-base">
                  Enable Internal Repository Matching
                </Label>
                <p className="text-sm text-muted-foreground">
                  Compare submissions against previous student documents
                </p>
              </div>
              <Switch
                id="enable_internal_repo"
                checked={settings.enable_internal_repo}
                onCheckedChange={(checked) => handleChange('enable_internal_repo', checked)}
                data-testid="enable-internal-repo-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="min-w-32"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default AdminSettings;
